/**
 * Returns the number rounded to the nearest interval.
 * Example:
 *
 *   roundToNearest(1000.5, 1) // 1000
 *   roundToNearest(1000.5, 0.5)  // 1000.5
 *
 * @param {number} value    The number to round
 * @param {number} interval The numeric interval to round to
 * @return {number}
 */
export const roundToNearest = (value, interval) => {
  return Math.floor(value / interval) * interval
}

/**
 * Applies updates to the raw order data.
 *
 * @param {Array} rawOrders - The existing raw orders.
 * @param {Array} newOrders - The new orders to apply.
 * @returns {Array} - The updated raw orders.
 */
export const applyUpdatesToRawData = (rawOrders, newOrders) => {
  // console.log(rawOrders)
  // Convert rawOrders to a map for efficient updates
  const orderMap = new Map(rawOrders.map(order => [order[0], order[1]]))

  // Apply updates from newOrders
  if (!newOrders) {
    return rawOrders
  }
  for (const [price, volume] of newOrders) {
    if (volume === 0) {
      // Remove the order if volume is zero
      orderMap.delete(price)
    } else {
      // Add or update the order
      orderMap.set(price, volume)
    }
  }

  // Convert the map back to an array of orders
  return Array.from(orderMap).map(([price, volume]) => [price, volume])
}

export const sortAsks = asks => {
  return asks.sort((a, b) => Number(a.price) - Number(b.price))
}
export const sortBids = bids => {
  return bids.sort((a, b) => Number(b.price) - Number(a.price))
}

export const getMaxTotalSum = orders => {
  // Map through orders, parse the total as a float, and filter out invalid numbers
  const totalSums = orders
    .map(order => parseFloat(order.total)) // Parse each total (price * amount)
    .filter(total => !isNaN(total)) // Filter out NaN values

  // console.log(orders)
  // console.log(totalSums)
  // console.log(Math.max(...totalSums))

  // If there are valid numbers, find the max, otherwise return 0
  return totalSums.length > 0 ? Math.max(...totalSums) : 0
}

export const addDepths = (orders, maxTotal) => {
  return orders.map(order => {
    const calculatedTotal = parseFloat(order.total) // Use order.total, not order[1]
    const depth = (calculatedTotal / maxTotal) * 100

    // Directly modify the order object
    const updatedOrder = { ...order, depth } // Add depth to the order object
    // console.log(updatedOrder)

    return updatedOrder
  })
}

/**
 * Sorts and filters order data.
 *
 * @param {Array} orders - The orders to be processed.
 * @param {number} lastTrade - The price of the last trade for filtering purposes.
 * @param {string} type - The type of orders ('asks' or 'bids').
 * @returns {Array} - The sorted and filtered orders.
 */
export const sortAndFilterOrders = (orders, lastTrade, type) => {
  // Define sorting logic
  const sortFunction =
    type === 'asks'
      ? (a, b) => a.price - b.price // For asks, sort by price in ascending order
      : (a, b) => b.price - a.price // For bids, sort by price in descending order

  // Sort the orders
  const sortedOrders = orders.sort(sortFunction)
  // console.log(sortedOrders)
  // If lastTrade is defined, filter the orders
  if (lastTrade !== undefined) {
    // const threshold = 0.1 // Define a threshold, e.g., 10%
    return sortedOrders.filter(order => {
      // console.log(order)
      const price = order.price // Access price property of each order object
      return price
      // return type === 'bids'
      //   ? price >= lastTrade // For asks, filter out prices too high
      //   : price <= lastTrade // For bids, filter out prices too low
    })
  }

  return sortedOrders
}

/**
 * Groups price levels by their price
 * Example:
 *
 *  groupByPrice([ [1000, 100], [1000, 200], [993, 20] ]) // [ [ 1000, 300 ], [ 993, 20 ] ]
 *
 * @param {number[][]} levels
 * @return {number[][]}
 */
export const groupByPrice = levels => {
  const groupedLevels = {}

  levels.forEach(level => {
    const [price, volume] = level
    // console.log(level)
    if (!groupedLevels[price]) {
      groupedLevels[price] = 0
    }
    groupedLevels[price] += parseFloat(volume)
  })
  // console.log(groupedLevels)
  return Object.entries(groupedLevels).map(([price, totalVolume]) => [parseFloat(price), parseFloat(totalVolume)])
}
export const groupByPricme = levels => {
  const grouped = levels.reduce((acc, level) => {
    const price = parseFloat(level[0])
    const size = parseFloat(level[1])

    if (acc[price]) {
      acc[price] += size
    } else {
      acc[price] = size
    }

    return acc
  }, {})

  return Object.entries(grouped).map(([price, size]) => [parseFloat(price), size])
}

/**
 * Group price levels by given ticket size. Uses groupByPrice() and roundToNearest()
 * Example:
 *
 * groupByTicketSize([ [1000.5, 100], [1000, 200], [993, 20] ], 1) // [[1000, 300], [993, 20]]
 *
 * @param {number[][]} levels
 * @param {number} ticketSize
 * @return {number[][]}
 */
export const groupByTicketSize = (levels, ticketSize) => {
  // console.log(groupByPrice(levels.map(level => [roundToNearest(level[0], ticketSize), level[1]])))
  return groupByPrice(levels.map(level => [roundToNearest(level.price, ticketSize), level.amount]))
}

export const formatNumber = arg => {
  return new Intl.NumberFormat('en-US').format(arg)
}

// useEffect(() => {
//   console.log('lastTrade', lastTrade)
// }, [lastTrade])

// useEffect(() => {
//   if (!selectedMarket.id) return

//   let ws
//   let reconnectAttempts = 0
//   const maxReconnectAttempts = 5
//   const reconnectDelay = 5000 // 5 seconds

//   const connectWebSocket = () => {
//     // Establish WebSocket connection
//     ws = new WebSocket(
//       window.location.host.includes('localhost')
//         ? `ws://${window.location.host}/api/v2/ws/public`
//         : `wss://${window.location.host}/api/v2/ws/public`
//     )

//     ws.onopen = () => {
//       reconnectAttempts = 0 // Reset reconnect attempts on successful connection

//       setTimeout(() => {
//         const subscribeMsg = {
//           event: 'subscribe',
//           streams: [
//             `${selectedMarket.id}.ob-snap`, // For snapshot data
//             `${selectedMarket.id}.ob-inc`, // For incremental updates
//           ],
//         }
//         ws.send(JSON.stringify(subscribeMsg)) // Send subscribe message after 2 seconds
//       }, 2000) // 2000ms = 2 seconds
//     }

//     ws.onclose = () => {
//       // If the connection is closed and retry attempts are less than the max
//       if (reconnectAttempts < maxReconnectAttempts) {
//         reconnectAttempts++
//         setTimeout(connectWebSocket, reconnectDelay) // Retry after delay
//       } else {
//         console.log("Max reconnect attempts reached, no more retries.")
//       }
//     }

//     ws.onmessage = (event) => {
//       const data = JSON.parse(event.data)

//       setOrderBook((prevBook) => {
//         const newBook = {
//           asks: [...prevBook.asks],
//           bids: [...prevBook.bids],
//         }

//         // Apply snapshot data (ob-snap) for asks and bids
//         if (data[`${selectedMarket.id}.ob-snap`]) {
//           const snapshotAsks = data[`${selectedMarket.id}.ob-snap`]?.asks || []
//           const snapshotBids = data[`${selectedMarket.id}.ob-snap`]?.bids || []

//           newBook.asks = snapshotAsks.map(([price, amount]) => ({ price, amount, total: price * amount }))
//           newBook.bids = snapshotBids.map(([price, amount]) => ({ price, amount, total: price * amount }))
//         }

//         // newBook.asks = [...prevBook.asks, ...newBook.asks]
//         // newBook.bids = [...prevBook.bids, ...newBook.bids]
//         // console.log(data[`${selectedMarket.id}.ob-inc`]?.asks)
//         // console.log(newBook.asks.length)
//         console.log(newBook.bids.length)
//         // Apply incremental updates (ob-inc) for asks and bids
//         if (data[`${selectedMarket.id}.ob-inc`]) {
//           data[`${selectedMarket.id}.ob-inc`]?.asks?.forEach(([price, amount]) => {
//             if (amount === 0) {
//               newBook.asks = newBook.asks.filter((ask) => ask.price !== price)
//             } else {
//               const index = newBook.asks.findIndex((ask) => ask.price === price)
//               const newOrder = { price, amount, total: price * amount }
//               if (index === -1) {
//                 newBook.asks.push(newOrder)
//               } else {
//                 newBook.asks[index] = newOrder
//               }
//             }
//           })

//           data[`${selectedMarket.id}.ob-inc`]?.bids?.forEach(([price, amount]) => {
//             if (amount === 0) {
//               newBook.bids = newBook.bids.filter((bid) => bid.price !== price)
//             } else {
//               const index = newBook.bids.findIndex((bid) => bid.price === price)
//               const newOrder = { price, amount, total: price * amount }
//               if (index === -1) {
//                 newBook.bids.push(newOrder)
//               } else {
//                 newBook.bids[index] = newOrder
//               }
//             }
//           })
//         }

//         // Filter asks and bids based on lastTrade price
//         if (lastTrade && lastTrade.price) {
//           newBook.asks = newBook.asks.filter((ask) => ask.price > lastTrade.price)
//           newBook.bids = newBook.bids.filter((bid) => bid.price < lastTrade.price)
//         }

//         // Group and sort the orders, then limit the results to 20 entries
//         newBook.asks = groupOrders(newBook.asks, grouping)
//         console.log(newBook.bids)
//         newBook.bids = groupOrders(newBook.bids, grouping)
//         console.log(newBook.bids)

//         const sortedAndFilteredAsks = sortAndFilterOrders(newBook.asks, lastTrade, 'asks')
//         const sortedAndFilteredBids = sortAndFilterOrders(newBook.bids, lastTrade, 'bids')
//         console.log(sortedAndFilteredBids)
//         // const sortedAndFilteredAsks = (newBook.asks).slice(0, MAX_ORDER_BOOK_ENTRIES)
//         // const sortedAndFilteredBids = (newBook.bids).slice(0, MAX_ORDER_BOOK_ENTRIES)

//         const maxTotalAsks = getMaxTotalSum(sortedAndFilteredAsks)
//         const maxTotalBids = getMaxTotalSum(sortedAndFilteredBids)
//         // console.log(maxTotalAsks)
//         // console.log(maxTotalBids)
//         newBook.asks = addDepths(sortedAndFilteredAsks, maxTotalAsks)
//         newBook.bids = addDepths(sortedAndFilteredBids, maxTotalBids)
//         // console.log(newBook.asks)
//         if (newBook.asks.length > 500) {
//           newBook.asks = newBook.asks.slice(0, 500)
//         }
//         if (newBook.bids.length > 500) {
//           newBook.bids = newBook.bids.slice(0, 500)
//         }
//         // console.log(sortedAndFilteredAsks.length)
//         // console.log(sortedAndFilteredBids.length)

//         return newBook
//       })
//     }
//   }

//   connectWebSocket() // Initial connection

//   return () => {
//     if (ws.readyState === WebSocket.OPEN) {
//       ws.close()
//     }
//   }
// }, [selectedMarket.id])
